import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Alert from '@mui/material/Alert';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TableViewIcon from '@mui/icons-material/TableView';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import enGB from 'date-fns/locale/en-GB';
import { startOfDay, endOfDay } from 'date-fns';
import { DataGrid } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery, reportDownload } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getSiteStatus, downloadReport } from 'src/api/admin/reports';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/reports/site-status')({
	component: SiteStatusPage,
});

interface SearchParams {
	date?: string;
}

function SiteStatusPage() {
	const { user: authenticatedUser } = useAuthenticatedUser();
	const navigate = useNavigate();
	const search = useSearch({ strict: false }) as SearchParams;
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();
	const [menuAnchor, setMenuAnchor] = useState<(EventTarget & HTMLButtonElement) | null>(null);

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasPermission = verifyPermission([PERMISSIONS.adminReportsView]);

	useBreadcrumbLevel(0, 'Reports');
	useBreadcrumbLevel(1, 'Site Status');

	// Parse date value from URL or use today's date
	const dateParam = search.date;
	const date = useMemo(() => (dateParam ? new Date(dateParam) : new Date()), [dateParam]);

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['reports-site-status', dateParam],
		queryFn: () =>
			getSiteStatus({
				dateFrom: startOfDay(date).toISOString(),
				dateTo: endOfDay(date).toISOString(),
			}),
		enabled: hasPermission,
	});

	const isPDFDownloadDisabled = (data?.data?.length || 0) > 500;

	const downloadCSVReport = useCallback(async () => {
		const query = constructQuery({
			dateFrom: startOfDay(date).toISOString(),
			dateTo: endOfDay(date).toISOString(),
		});
		const blob = await downloadReport('site-status', query);
		if (blob) {
			reportDownload(blob);
		}
	}, [date]);

	const mappedColumns = useMemo(() => {
		if (!data?.columns) return [];
		return data.columns.map((c) => ({
			field: c.id,
			headerName: c.label,
			flex: 1,
			sortable: false,
		}));
	}, [data?.columns]);

	// Redirect if no permissions
	useEffect(() => {
		if (!hasPermission) {
			navigate({
				to: '/403',
				search: { permissions: 'adminReportsView', occurredAt: '/reports/site-status', method: 'GET', uuid: undefined },
			});
		}
	}, [hasPermission, navigate]);

	if (!hasPermission) {
		return null;
	}

	const revalidateSiteStatus = () => {
		queryClient.invalidateQueries({ queryKey: ['reports-site-status'] });
		refetch();
	};

	const handleDateChange = (newDate: Date | null) => {
		if (newDate) {
			updateQueryParams({ date: endOfDay(newDate).toISOString() });
		}
	};

	// Limit data to 100 rows if more than 500
	const displayData = isPDFDownloadDisabled ? (data?.data || []).slice(0, 100) : data?.data || [];

	return (
		<div className="p-4 flex flex-col gap-4">
			<div className="flex flex-row flex-wrap gap-2 w-full justify-between">
				<div className="flex flex-row gap-2">
					<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
						<DatePicker
							value={date}
							onChange={handleDateChange}
							slotProps={{
								textField: {
									size: 'small',
								},
							}}
						/>
					</LocalizationProvider>
				</div>
				<Button onClick={(e) => setMenuAnchor(e.currentTarget)}>
					<MoreVertIcon />
				</Button>

				<Menu
					anchorEl={menuAnchor}
					open={!!menuAnchor}
					onClose={() => setMenuAnchor(null)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					elevation={1}
				>
					<MenuItem
						key={'Export to Excel'}
						onClick={() => {
							downloadCSVReport();
							setMenuAnchor(null);
						}}
					>
						<ListItemIcon>
							<TableViewIcon />
						</ListItemIcon>
						<ListItemText>Export to Excel</ListItemText>
					</MenuItem>
				</Menu>
			</div>
			{isPDFDownloadDisabled && (
				<Alert severity="info" variant="filled">
					This dataset is a bit hefty with over 500 records. We&apos;re showing you the first 100. If you need more
					please use the export to Excel option.
				</Alert>
			)}
			<Paper variant="outlined">
				<DataGrid
					data={displayData}
					columns={mappedColumns}
					isLoading={isLoading}
					paginationMode="server"
					sortingMode="server"
					sortingOrder={['asc', 'desc']}
					pagination
					rowCount={displayData.length}
					totalRecordsCount={data?.data?.length ?? 0}
					onReload={revalidateSiteStatus}
					getRowClassName={() => '!cursor-default'}
				/>
			</Paper>
		</div>
	);
}
